/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ContextualHelp, Skeleton, Typography2 } from '@coursera/cds-core';
import { CheckIcon, CloseIcon } from '@coursera/cds-icons';
import { useVisibilityTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/sdk';

import _t from 'i18n!nls/personalization-settings';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-150);
  `,
  captionLead: css`
    color: var(--cds-color-emphasis-quaternary-content-default);
  `,
  captionHint: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  list: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    margin: 0;
    padding: 0;
    list-style: none;
  `,
  // Chip-shaped, but deliberately not a CDS Chip: Chip is a single button, and
  // an inferred value carries two independent actions. The white fill keeps an
  // unconfirmed suggestion from outweighing the values the learner chose --
  // a filled purple wash reads as CDS's selected state, i.e. already accepted.
  value: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 6px 0 14px;
    border: 1px solid var(--cds-color-emphasis-quaternary-stroke-default);
    border-radius: var(--cds-border-radius-max);
    background-color: var(--cds-color-neutral-background-primary);
    color: var(--cds-color-emphasis-quaternary-content-default);
  `,
  action: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 26px;
    height: 26px;
    padding: 0;
    border-radius: var(--cds-border-radius-max);
    background-color: var(--cds-color-neutral-background-primary);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,
  confirm: css`
    border: 1px solid var(--cds-color-interactive-primary);
    color: var(--cds-color-interactive-primary);

    &:hover {
      background-color: var(--cds-color-interactive-primary);
      color: var(--cds-color-neutral-primary-invert);
    }

    &:active {
      border-color: var(--cds-color-interactive-primary-pressed);
      background-color: var(--cds-color-interactive-primary-pressed);
    }
  `,
  valueSkeleton: css`
    border-radius: var(--cds-border-radius-max);
  `,
  reject: css`
    border: 1px solid var(--cds-color-neutral-disabled-strong);
    color: var(--cds-color-interactive-secondary);

    &:hover {
      border-color: var(--cds-color-interactive-secondary);
      background-color: var(--cds-color-interactive-background-secondary-hover-weak);
    }
  `,
};

/** Pill widths for the loading placeholder, varied so it reads as a row of names. */
const SKELETON_VALUE_WIDTHS = [128, 152, 112];

type InferredValueItemProps<V extends ReviewableValue> = {
  value: V;
  impression: EventData<'view_button'>;
  registerConfirmButton: (valueId: string, button: HTMLButtonElement | null) => void;
  onConfirm: (value: V) => void;
  onReject: (value: V) => void;
};

/** One value's pill; its own component so each pill gets its own visibility tracker. */
const InferredValueItem = <V extends ReviewableValue>({
  value,
  impression,
  registerConfirmButton,
  onConfirm,
  onReject,
}: InferredValueItemProps<V>) => {
  const impressionRef = useVisibilityTracker<HTMLLIElement, 'view_button'>('view_button', impression);

  return (
    <li ref={impressionRef}>
      <Typography2 component="span" variant="bodySecondary" css={styles.value}>
        {value.name}
        <button
          type="button"
          ref={(button) => registerConfirmButton(value.id, button)}
          css={[styles.action, styles.confirm]}
          aria-label={_t('Confirm #{valueName}', { valueName: value.name })}
          onClick={() => onConfirm(value)}
          data-testid={`inferred-value-review-confirm-${value.id}`}
        >
          <CheckIcon size="small" />
        </button>
        <button
          type="button"
          css={[styles.action, styles.reject]}
          aria-label={_t('Reject #{valueName}', { valueName: value.name })}
          onClick={() => onReject(value)}
          data-testid={`inferred-value-review-reject-${value.id}`}
        >
          <CloseIcon size="small" />
        </button>
      </Typography2>
    </li>
  );
};

export type ReviewableValue = {
  id: string;
  name: string;
};

export type InferredValuesReviewProps<V extends ReviewableValue> = {
  /** The inferred values to show, in the order they should be read. */
  values: V[];
  /**
   * Names the group for assistive tech, e.g. "Roles based on your activity".
   * The visible caption is attribute-agnostic, so this is what tells a screen
   * reader user which setting the group belongs to.
   */
  ariaLabel: string;
  /**
   * Attribute-specific half of the caption, e.g. "— confirm the roles that fit".
   * Rendered after the shared "Based on your activity" lead. Pass the separator
   * as part of the string so translators can drop or replace it per locale
   * rather than receiving a placeholder with a bare dash around it.
   */
  actionHint: string;
  /** Where these values came from, revealed by the caption's info toggle. */
  explanation: string;
  onConfirm: (value: V) => void;
  onReject: (value: V) => void;
  /** Builds the `view_button` impression fired once when a value first scrolls into view. */
  impressionFor: (value: V) => EventData<'view_button'>;
  /**
   * Moves focus to a value's confirm button each time `key` changes, e.g. to the
   * next value after the focused one is confirmed and removed.
   */
  focusRequest?: { valueId: string; key: number };
  /** Shows a placeholder shaped like the caption and a row of values. */
  loading?: boolean;
};

/**
 * Inferred values a learner can confirm or reject, rather than only read.
 *
 * Renders nothing when there are no values once loaded -- an empty aggregate and an
 * inapplicable one look the same to a learner, and neither warrants a caption
 * with nothing under it.
 *
 * Attribute-agnostic so each setting row that gains an aggregate can reuse it.
 */
const InferredValuesReview = <V extends ReviewableValue>({
  values,
  ariaLabel,
  actionHint,
  explanation,
  onConfirm,
  onReject,
  impressionFor,
  focusRequest,
  loading,
}: InferredValuesReviewProps<V>) => {
  const confirmButtonsRef = React.useRef(new Map<string, HTMLButtonElement>());

  React.useEffect(() => {
    if (focusRequest) confirmButtonsRef.current.get(focusRequest.valueId)?.focus();
  }, [focusRequest]);

  const registerConfirmButton = React.useCallback<(valueId: string, button: HTMLButtonElement | null) => void>(
    (valueId, button) => {
      if (button) confirmButtonsRef.current.set(valueId, button);
      else confirmButtonsRef.current.delete(valueId);
    },
    []
  );

  if (loading) {
    return (
      <div css={styles.container} role="status" aria-label={_t('Loading suggestions')}>
        <Skeleton variant="text" width={240} />
        <div css={styles.list}>
          {SKELETON_VALUE_WIDTHS.map((width) => (
            <Skeleton key={width} variant="rect" width={width} height={34} css={styles.valueSkeleton} />
          ))}
        </div>
      </div>
    );
  }

  if (values.length === 0) return null;

  return (
    <div css={styles.container} data-testid="inferred-values-review">
      <ContextualHelp
        label={
          <React.Fragment>
            <Typography2 component="span" variant="subtitleSmall" css={styles.captionLead} aria-hidden="true">
              {_t('Based on your activity')}
            </Typography2>{' '}
            {/* Not hidden from assistive tech: unlike the lead, the hint is the only
                place the confirm/reject affordance is explained. */}
            <Typography2 component="span" variant="bodySecondary" css={styles.captionHint}>
              {actionHint}
            </Typography2>
          </React.Fragment>
        }
        helpToggleProps={{ 'aria-label': _t('Why am I seeing these?') }}
      >
        <Typography2 component="p" variant="bodySecondary">
          {explanation}
        </Typography2>
      </ContextualHelp>

      <ul css={styles.list} aria-label={ariaLabel}>
        {values.map((value) => (
          <InferredValueItem
            key={value.id}
            value={value}
            impression={impressionFor(value)}
            registerConfirmButton={registerConfirmButton}
            onConfirm={onConfirm}
            onReject={onReject}
          />
        ))}
      </ul>
    </div>
  );
};

export default InferredValuesReview;
